/**
 * Frozen Egg to Success Rate Estimator
 * Implements:
 * - Age-only egg retrieval estimator (Templeton et al., 2004)
 * - Age + AMH egg retrieval estimator (Seoul National University Hospital, 2016)
 * - Three frozen-egg-to-live-birth models: Goldman, Doyle, and Cobo
 */

// ===============================
// Constants & Configuration
// ===============================
const THAW_SURVIVAL_DEFAULT = 0.904; // 90.4% warmed-egg survival
const DISPLAY_CAP_PERCENT = 99; // Avoid showing 100%

// ===============================
// Age-only egg retrieval estimator (Machtinger et al., 2025)
// ===============================

/**
 * Estimate mean number of oocytes retrieved from age alone.
 * Machtinger et al., 2025 (PMCID: PMC12428322):
 *   mean_oocytes_from_age(age_years) = 20 * (0.92 ** (age_years - 30))
 *
 * We clamp age to 20–45 and round to the nearest whole egg for display.
 */
export function estimateEggsFromAge(ageYears: number): number {
  const clampedAge = Math.max(20, Math.min(45, ageYears));
  const mean = 20 * Math.pow(0.92, clampedAge - 30);
  return Math.round(mean);
}

// ===============================
// Age + AMH egg retrieval estimator (Seoul National University Hospital, 2016)
// Paper: "Nomogram to predict the number of oocytes retrieved in controlled ovarian stimulation"
// PMC4925866
// ===============================

/**
 * Convert AMH from pmol/L to ng/mL
 * Conversion factor: 1 ng/mL = 7.14 pmol/L
 */
export function convertAmhToNgMl(
  value: number,
  unit: "pmol/L" | "ng/mL",
): number {
  if (unit === "ng/mL") return value;
  return value / 7.14;
}

/**
 * Convert AMH from ng/mL to pmol/L
 * Conversion factor: 1 ng/mL = 7.14 pmol/L
 */
export function convertAmhToPmolL(
  value: number,
  unit: "pmol/L" | "ng/mL",
): number {
  if (unit === "pmol/L") return value;
  return value * 7.14;
}

/**
 * Get AMH status based on value in ng/mL
 * Categories based on clinical guidelines:
 * - Lower Reserve: < 1.0 ng/mL
 * - Normal Reserve: 1.0 - 3.5 ng/mL
 * - High Reserve: > 3.5 ng/mL
 */
export type AmhStatus = "lower" | "normal" | "high";

export function getAmhStatus(amhNgMl: number): AmhStatus {
  if (amhNgMl < 1.0) return "lower";
  if (amhNgMl <= 3.5) return "normal";
  return "high";
}

export function getAmhStatusLabel(status: AmhStatus): string {
  switch (status) {
    case "lower":
      return "Lower Reserve";
    case "normal":
      return "Normal Reserve";
    case "high":
      return "High Reserve";
  }
}

export function getAmhDescription(status: AmhStatus): string {
  switch (status) {
    case "lower":
      return "A lower level of ovarian reserve activity, suggesting fewer follicles may be recruited during stimulation. This doesn't determine egg quality.";
    case "normal":
      return "A balanced, healthy level of ovarian reserve where a solid number of follicles are typically developing. This is associated with good response to stimulation.";
    case "high":
      return "A highly active ovarian environment, indicating a robust reserve with potential for higher egg yield. May require careful monitoring during stimulation.";
  }
}

/**
 * Estimate number of oocytes retrieved from age and AMH.
 *
 * AMH-based model derived from La Marca et al., Fertility and Sterility 2011:
 * https://www.fertstert.org/article/S0015-0282(11)00501-2/fulltext
 *
 * Formula: eggs = 5 × AMH (ng/mL) + 3
 */
export interface AmhEggEstimate {
  mean: number;
  low: number;
  high: number;
  status: AmhStatus;
  statusLabel: string;
  description: string;
}

export function estimateEggsFromAgeAndAmh(
  ageYears: number,
  amhValue: number,
  amhUnit: "pmol/L" | "ng/mL" = "ng/mL",
): AmhEggEstimate {
  const amhNgMl = convertAmhToNgMl(amhValue, amhUnit);
  const clampedAmh = Math.max(0.1, Math.min(10, amhNgMl));

  // AMH-based model: eggs = 5 × AMH + 3
  // Source: La Marca et al., Fertility and Sterility 2011
  const meanOocytes = 5 * clampedAmh + 3;

  // Add uncertainty range (±20% is typical clinical variance)
  const low = Math.max(1, Math.round(meanOocytes * 0.8));
  const high = Math.round(meanOocytes * 1.2);
  const mean = Math.round(meanOocytes);

  const status = getAmhStatus(clampedAmh);

  return {
    mean,
    low,
    high,
    status,
    statusLabel: getAmhStatusLabel(status),
    description: getAmhDescription(status),
  };
}

// ===============================
// Age + AMH + AFC egg retrieval estimator (Choi et al., 2016, Model 3)
// Paper: "Nomogram to predict the number of oocytes retrieved in
// controlled ovarian stimulation" — PMC4925866
// Poisson regression with log link, NO FSH term.
// ===============================

/**
 * Estimate retrieved oocytes from age, AMH, and AFC.
 * Choi 2016, Model 3:
 *   ln(oocytes) = 2.32 - 0.017*age + 0.039*AMH + 0.03*AFC
 * Units: AMH in ng/mL, AFC as integer count across both ovaries.
 * Coefficients verified from the source paper (see WebFetch of PMC4925866).
 */
export interface AgeAmhAfcEggEstimate {
  mean: number;
  low: number;
  high: number;
  modelType: "choi-2016-model-3";
  inputs: {
    age: number;
    amhNgMl: number;
    afc: number;
  };
}

export function estimateEggsFromAgeAmhAfc(
  ageYears: number,
  amhValue: number,
  amhUnit: "pmol/L" | "ng/mL",
  afcCount: number,
): AgeAmhAfcEggEstimate {
  const amhNgMl = convertAmhToNgMl(amhValue, amhUnit);
  // Soft clamps to keep the linear-in-marker model from producing absurd
  // yields when a user enters an unrealistic AMH (e.g. 100 ng/mL is almost
  // always a unit-confusion typo). AFC is naturally bounded.
  const clampedAmh = Math.max(0.1, Math.min(15, amhNgMl));
  const clampedAge = Math.max(18, Math.min(50, ageYears));
  const clampedAfc = Math.max(0, Math.min(60, Math.round(afcCount)));

  const lnMu =
    2.32 - 0.017 * clampedAge + 0.039 * clampedAmh + 0.03 * clampedAfc;
  const mu = Math.exp(lnMu);
  const clampedMu = Math.max(0, Math.min(40, mu));

  return {
    mean: Math.round(clampedMu),
    low: Math.max(0, Math.round(clampedMu * 0.8)),
    high: Math.min(40, Math.round(clampedMu * 1.2)),
    modelType: "choi-2016-model-3",
    inputs: {
      age: clampedAge,
      amhNgMl,
      afc: clampedAfc,
    },
  };
}

// ===============================
// Full-inputs egg retrieval estimator (Age + AMH + AFC + FSH)
// Negative Binomial regression (NB2) with log link
// ===============================

/**
 * Convert FSH to IU/L
 * Note: mIU/mL and IU/L are equivalent (1 mIU/mL = 1 IU/L)
 */
export function convertFshToIUL(
  value: number,
  unit: "mIU/mL" | "IU/L",
): number {
  // Both units are equivalent
  return value;
}

/**
 * Validation error type for full-inputs estimator
 */
export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Validate inputs for full-inputs estimator
 * Performs strict pre-model validation with clear error messages
 *
 * @param age - Age in years (already normalized)
 * @param amhNgMl - AMH in ng/mL (already converted from pmol/L if needed)
 * @param afc - Total antral follicle count 2-10mm across both ovaries
 * @param fshIUL - FSH in IU/L (already normalized from mIU/mL if needed)
 * @returns Array of validation errors (empty if all valid)
 */
export function validateFullInputs(
  age: number,
  amhNgMl: number,
  afc: number,
  fshIUL: number,
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Age validation - must be in years
  if (!Number.isFinite(age) || isNaN(age)) {
    errors.push({
      field: "age",
      message: "Age must be a valid number in years",
    });
  } else if (age < 18 || age > 50) {
    errors.push({
      field: "age",
      message: "Age must be between 18 and 50 years for accurate predictions",
    });
  }

  // AMH validation - must be in ng/mL and > 0
  if (!Number.isFinite(amhNgMl) || isNaN(amhNgMl)) {
    errors.push({
      field: "amh",
      message: "AMH must be a valid number in ng/mL",
    });
  } else if (amhNgMl <= 0) {
    errors.push({
      field: "amh",
      message:
        "AMH must be greater than 0 ng/mL. If provided in pmol/L, it will be converted using: ng/mL = pmol/L / 7.14",
    });
  } else if (amhNgMl > 50) {
    // Sanity check: extremely high AMH values may indicate conversion error
    errors.push({
      field: "amh",
      message:
        "AMH value seems unusually high (>50 ng/mL). Please verify the value and unit are correct.",
    });
  }

  // AFC validation - must be total 2-10mm follicles across both ovaries, integer >= 0
  if (!Number.isFinite(afc) || isNaN(afc)) {
    errors.push({
      field: "afc",
      message: "AFC must be a valid number",
    });
  } else if (!Number.isInteger(afc)) {
    errors.push({
      field: "afc",
      message: "AFC must be an integer (whole number)",
    });
  } else if (afc < 0) {
    errors.push({
      field: "afc",
      message: "AFC must be greater than or equal to 0",
    });
  } else if (afc > 100) {
    // Sanity check: extremely high AFC may indicate incorrect measurement
    errors.push({
      field: "afc",
      message:
        "AFC value seems unusually high (>100). Please verify this is the total count of 2-10mm follicles across both ovaries.",
    });
  }

  // Note: AFC should represent 2-10mm follicles specifically
  // If different size range is used, the model may not be accurate
  // This is a data quality requirement that should be communicated to users

  // FSH validation - must be in IU/L and > 0
  if (!Number.isFinite(fshIUL) || isNaN(fshIUL)) {
    errors.push({
      field: "fsh",
      message:
        "FSH must be a valid number in IU/L (or mIU/mL, which is equivalent)",
    });
  } else if (fshIUL <= 0) {
    errors.push({
      field: "fsh",
      message: "FSH must be greater than 0 IU/L",
    });
  } else if (fshIUL > 100) {
    // Sanity check: extremely high FSH may indicate unit error
    errors.push({
      field: "fsh",
      message:
        "FSH value seems unusually high (>100 IU/L). Please verify the value and unit are correct.",
    });
  }

  return errors;
}

/**
 * Result from full-inputs estimator
 *
 * IMPORTANT: This estimate assumes AFC represents 2-10mm follicles across both ovaries.
 * If a different follicle size range was measured, prediction accuracy may be affected.
 */
export interface FullInputsEggEstimate {
  mean: number; // Clamped to 0-40 range (clinically reasonable)
  rawMean: number; // Unclamped value from model (for transparency)
  low: number; // Lower bound of confidence range
  high: number; // Upper bound of confidence range
  modelType: "full-inputs";
  inputs: {
    age: number; // Age in years
    amhNgMl: number; // AMH in ng/mL (normalized)
    afc: number; // Total 2-10mm AFC across both ovaries
    fshIUL: number; // FSH in IU/L (normalized)
  };
}

/**
 * Estimate number of oocytes retrieved using full inputs (Age + AMH + AFC + FSH).
 *
 * UNIT NORMALIZATION (performed before any calculations):
 * - Age: Must be in years (no conversion needed)
 * - AMH: Converted to ng/mL. If provided in pmol/L, converts using: ng/mL = pmol/L / 7.14
 * - FSH: Normalized to IU/L (mIU/mL and IU/L are numerically equivalent, no conversion)
 * - AFC: Must be total antral follicle count of 2-10mm follicles across BOTH ovaries
 *
 * MODEL FORMULA (Negative Binomial regression NB2 with log link):
 * mu_hat = exp(2.4124 - 0.0713*age + 0.7708*ln(amh_ng_ml) + 0.4443*ln(afc_2_10mm) + 0.2849*ln(fsh_IU_L))
 *
 * SPECIAL CASES:
 * - If AFC = 0: uses ln(AFC + 1) to avoid log(0)
 * - Output clamped to clinically reasonable range of 0-40 eggs
 *
 * VALIDATION:
 * - All unit conversions performed BEFORE any logarithms
 * - Enforces: age 18-50, amh_ng_ml > 0, fsh_IU_L > 0, afc >= 0 (integer)
 * - Sanity checks for unusually high values that may indicate unit errors
 * - Throws clear validation error if any input is missing, ambiguous, or invalid
 *
 * AFC REQUIREMENT:
 * This model expects AFC to represent 2-10mm follicles specifically. If a different
 * follicle size range is used, the prediction accuracy may be compromised. The function
 * does not attempt to adjust for different size ranges.
 *
 * @param ageYears - Patient age in years (must be 18-50)
 * @param amhValue - AMH value (will be converted to ng/mL if needed)
 * @param amhUnit - AMH unit: "pmol/L" (will convert) or "ng/mL" (use directly)
 * @param afcCount - Total 2-10mm antral follicle count across both ovaries (integer >= 0)
 * @param fshValue - FSH value (will be normalized to IU/L)
 * @param fshUnit - FSH unit: "mIU/mL" or "IU/L" (equivalent, will normalize to IU/L)
 * @returns Estimated egg count with uncertainty range
 * @throws Error with clear validation message if inputs are invalid
 */
export function estimateEggsFromFullInputs(
  ageYears: number,
  amhValue: number,
  amhUnit: "pmol/L" | "ng/mL",
  afcCount: number,
  fshValue: number,
  fshUnit: "mIU/mL" | "IU/L",
): FullInputsEggEstimate {
  // STEP 1: Normalize all inputs to required units BEFORE any calculations
  // This ensures consistent units going into the model

  // AMH: Convert to ng/mL if provided in pmol/L
  // Conversion: ng/mL = pmol/L / 7.14
  const amhNgMl = convertAmhToNgMl(amhValue, amhUnit);

  // FSH: Normalize to IU/L (mIU/mL and IU/L are numerically equivalent)
  const fshIUL = convertFshToIUL(fshValue, fshUnit);

  // AFC: Must already be in correct format (total 2-10mm follicles across both ovaries)
  // No conversion applied - user must provide correct measurement
  const afc_2_10mm_total = afcCount;

  // STEP 2: Strict pre-model validation on normalized inputs
  // All conversions completed above, now validate before applying logarithms
  const validationErrors = validateFullInputs(
    ageYears,
    amhNgMl,
    afc_2_10mm_total,
    fshIUL,
  );
  if (validationErrors.length > 0) {
    const errorMessages = validationErrors
      .map((e) => `${e.field}: ${e.message}`)
      .join("; ");
    throw new Error(`Validation failed: ${errorMessages}`);
  }

  // STEP 3: Apply model coefficients and transformations
  // NB2 regression coefficients (calibrated from clinical data)
  const beta0 = 2.4124; // Intercept
  const betaAge = -0.0713; // Age coefficient (negative = fewer eggs with age)
  const betaAmh = 0.7708; // AMH coefficient (positive = more eggs with higher AMH)
  const betaAfc = 0.4443; // AFC coefficient (positive = more eggs with higher AFC)
  const betaFsh = 0.2849; // FSH coefficient (positive in this model formulation)

  // Handle AFC = 0 special case to avoid log(0)
  // When AFC = 0, use ln(1) = 0, effectively removing AFC contribution
  const afcForLog = afc_2_10mm_total === 0 ? 1 : afc_2_10mm_total;

  // Calculate natural logarithms for the model
  // All inputs are now in correct units and validated as positive (or AFC = 0 handled)
  const lnAmh = Math.log(amhNgMl); // ln(ng/mL)
  const lnAfc = Math.log(afcForLog); // ln(count) or ln(1) if AFC = 0
  const lnFsh = Math.log(fshIUL); // ln(IU/L)

  // Guard against numerical issues in log calculations
  if (
    !Number.isFinite(lnAmh) ||
    !Number.isFinite(lnAfc) ||
    !Number.isFinite(lnFsh)
  ) {
    throw new Error(
      "Numerical error: invalid log calculation. This should not occur after validation.",
    );
  }

  // Calculate linear predictor
  const linearPredictor =
    beta0 +
    betaAge * ageYears +
    betaAmh * lnAmh +
    betaAfc * lnAfc +
    betaFsh * lnFsh;

  // Check for numerical issues before exp
  if (!Number.isFinite(linearPredictor)) {
    throw new Error("Numerical error: invalid linear predictor");
  }

  // Calculate mu_hat using exp (inverse of log link)
  const muHat = Math.exp(linearPredictor);

  // Check for numerical issues in result
  if (!Number.isFinite(muHat) || isNaN(muHat)) {
    throw new Error("Numerical error: invalid model output");
  }

  // Store raw value before clamping
  const rawMean = muHat;

  // Clamp to clinically reasonable range (0-40)
  const clampedMean = Math.max(0, Math.min(40, muHat));

  // Calculate uncertainty range (±20% is typical clinical variance)
  const low = Math.max(0, Math.round(clampedMean * 0.8));
  const high = Math.min(40, Math.round(clampedMean * 1.2));
  const mean = Math.round(clampedMean);

  return {
    mean,
    rawMean,
    low,
    high,
    modelType: "full-inputs",
    inputs: {
      age: ageYears,
      amhNgMl,
      afc: afcCount,
      fshIUL,
    },
  };
}

// ===============================
// Doyle 2016 Data (digitized from curves)
// ===============================
const DOYLE_TABLE = {
  "30-34": {
    eggs: [5, 10, 15, 20, 25, 30],
    p_ge1: [0.35, 0.6, 0.75, 0.83, 0.88, 0.92],
    p_ge2: [0.05, 0.2, 0.35, 0.48, 0.6, 0.7],
  },
  "35-37": {
    eggs: [5, 10, 15, 20, 25, 30],
    p_ge1: [0.25, 0.5, 0.68, 0.78, 0.85, 0.9],
    p_ge2: [0.03, 0.15, 0.28, 0.4, 0.52, 0.64],
  },
  "38-40": {
    eggs: [5, 10, 15, 20, 25, 30],
    p_ge1: [0.12, 0.32, 0.48, 0.6, 0.68, 0.74],
    p_ge2: [0.01, 0.06, 0.12, 0.2, 0.28, 0.36],
  },
  "41-42": {
    eggs: [5, 10, 15, 20, 25, 30],
    p_ge1: [0.06, 0.16, 0.28, 0.38, 0.46, 0.54],
    p_ge2: [0.0, 0.02, 0.05, 0.09, 0.13, 0.18],
  },
} as const;

const DOYLE_BANDS: { band: string; lo: number; hi: number }[] = [
  { band: "30-34", lo: 30, hi: 34 },
  { band: "35-37", lo: 35, hi: 37 },
  { band: "38-40", lo: 38, hi: 40 },
  { band: "41-42", lo: 41, hi: 42 },
];

// ===============================
// Cobo 2016 Data (per-egg rates)
// ===============================
const COBO_RATE = {
  "≤35": 0.066, // 6.6% per warmed egg
  "36-37": 0.061, // 6.1%
  "38-39": 0.056, // 5.6%
  "≥40": 0.018, // 1.8%
} as const;

const COBO_BANDS: { band: string; lo: number | null; hi: number | null }[] = [
  { band: "≤35", lo: null, hi: 35 },
  { band: "36-37", lo: 36, hi: 37 },
  { band: "38-39", lo: 38, hi: 39 },
  { band: "≥40", lo: 40, hi: null },
];

// ===============================
// Helper Functions
// ===============================
function pct(x: number): number {
  if (!isFinite(x)) return 0;
  x = Math.max(0.0, Math.min(1.0, x));
  return Math.max(0, Math.min(DISPLAY_CAP_PERCENT, Math.round(100.0 * x)));
}

function interp(
  x: number,
  xs: readonly number[],
  ys: readonly number[],
): number {
  if (xs.length === 0 || ys.length === 0) return 0;
  if (xs.length !== ys.length) return 0;

  const first = xs[0];
  const last = xs[xs.length - 1];
  if (first === undefined || last === undefined) return 0;

  if (x <= first) return ys[0] ?? 0;
  if (x >= last) return ys[ys.length - 1] ?? 0;

  for (let i = 0; i < xs.length - 1; i++) {
    const xi = xs[i];
    const xi1 = xs[i + 1];
    const yi = ys[i];
    const yi1 = ys[i + 1];

    if (
      xi === undefined ||
      xi1 === undefined ||
      yi === undefined ||
      yi1 === undefined
    ) {
      continue;
    }

    if (xi <= x && x <= xi1) {
      const t = (x - xi) / (xi1 - xi);
      return yi * (1 - t) + yi1 * t;
    }
  }

  const lastY = ys[ys.length - 1];
  return lastY ?? 0;
}

function mapAgeToDoyle(age: number): string | null {
  if (age < 30 || age > 42) return null;
  for (const { band, lo, hi } of DOYLE_BANDS) {
    if (age >= lo && age <= hi) return band;
  }
  return null;
}

function mapAgeToCobo(age: number): string | null {
  if (age < 30 || age > 42) return null;
  for (const { band, lo, hi } of COBO_BANDS) {
    if ((lo === null || age >= lo) && (hi === null || age <= hi)) {
      return band;
    }
  }
  return null;
}

// ===============================
// Model 1: Goldman Formula
// ===============================
// NB: `eggs` here is expected to be POST-THAW (already reduced by thaw
// survival at the call site — see calculateSuccessRate). We do NOT apply
// p_thaw again here — that would double-count survival.
export function goldmanLiveBirthProb(age: number, eggs: number): number {
  const p_blast = Math.max(0, -0.0085 * age + 0.73);
  const p_euploid = 1 / (1 + Math.exp(0.28 * (age - 35)));
  const p_live_given_euploid = 0.6;

  const p_success_per_egg = p_blast * p_euploid * p_live_given_euploid;
  const p_at_least_one_live_birth = 1 - Math.pow(1 - p_success_per_egg, eggs);

  return p_at_least_one_live_birth;
}

// ===============================
// Model 2: Doyle Formula
// ===============================
export interface DoyleResult {
  band: string | null;
  p_ge1: number; // P(≥1 live birth) as percentage
  p_ge2: number; // P(≥2 live births) as percentage
  available: boolean;
}

export function doyleProbs(age: number, eggsUsed: number): DoyleResult {
  const band = mapAgeToDoyle(age);

  if (!band || age < 30 || age > 42) {
    return {
      band: null,
      p_ge1: 0,
      p_ge2: 0,
      available: false,
    };
  }

  const tbl = DOYLE_TABLE[band as keyof typeof DOYLE_TABLE];
  const n = Math.max(0, Math.min(eggsUsed, 60));

  if (n === 0) {
    return {
      band,
      p_ge1: 0,
      p_ge2: 0,
      available: true,
    };
  }

  // Clamp to knot range 5-30 for interpolation
  const x = Math.max(5, Math.min(n, 30));
  const p1 = interp(x, tbl.eggs, tbl.p_ge1);
  const p2 = interp(x, tbl.eggs, tbl.p_ge2);

  return {
    band,
    p_ge1: pct(p1),
    p_ge2: pct(p2),
    available: true,
  };
}

// ===============================
// Model 3: Cobo Formula
// ===============================
// Cobo uses per-warmed-egg live-birth rate by age band + binomial calculation
// Method: P(≥1) = 1 - (1 - p)^n where p is the per-egg rate for the age band

export interface CoboResult {
  band: string | null;
  p_ge1: number; // P(≥1 live birth) as percentage
  p_ge2: number; // P(≥2 live births) as percentage
  available: boolean;
}

export function coboClbrGe1(age: number, eggsUsed: number): CoboResult {
  // Cobo model works for ages 30-42 (matching Doyle range)
  if (age < 30 || age > 42) {
    return {
      band: null,
      p_ge1: 0,
      p_ge2: 0,
      available: false,
    };
  }

  const band = mapAgeToCobo(age);
  if (!band) {
    return {
      band: null,
      p_ge1: 0,
      p_ge2: 0,
      available: false,
    };
  }

  // Get per-egg rate for this age band
  const p = COBO_RATE[band as keyof typeof COBO_RATE];
  const n = Math.max(0, eggsUsed);

  if (n === 0) {
    return {
      band,
      p_ge1: 0,
      p_ge2: 0,
      available: true,
    };
  }

  // Binomial calculation for ≥1 live birth
  // P(≥1) = 1 - P(0) = 1 - (1 - p)^n
  const qn = Math.pow(1.0 - p, n); // P(0 live births)
  const ge1 = 1.0 - qn; // P(≥1 live birth)

  // Binomial calculation for ≥2 live births
  // P(≥2) = 1 - P(0) - P(1) = 1 - q^n - n*p*(1-p)^(n-1)
  const ge2 = 1.0 - qn - n * p * Math.pow(1.0 - p, n - 1); // P(≥2 live births)

  return {
    band,
    p_ge1: pct(ge1),
    p_ge2: pct(ge2),
    available: true,
  };
}

// ===============================
// Main Calculator Function
// ===============================
export interface CalculationResult {
  goldman: {
    p_ge1: number;
    available: boolean;
  };
  doyle: DoyleResult;
  cobo: CoboResult;
  combined: {
    p_ge1: number; // Average of available models for ≥1 live birth
    p_ge2: number; // Average of available models for ≥2 live births
    availableCount: number;
  };
}

export function calculateSuccessRate(
  age: number,
  frozenEggs: number,
): CalculationResult {
  // Convert frozen eggs to eggs used (apply thaw survival)
  const eggsUsed = Math.floor(frozenEggs * THAW_SURVIVAL_DEFAULT);

  // Doyle & Cobo are only calibrated on ages 30-42. For users outside that
  // range we extrapolate to the nearest band — freezing at 25 doesn't
  // outperform freezing at 30 clinically, so treating <30 as 30 is
  // defensible and stops the ensemble from silently falling back to the
  // (idealized, optimistic) Goldman model at young ages.
  const modelAge = Math.max(30, Math.min(42, age));

  // Goldman (always available) — kept for reference; uses the real age.
  const goldmanP1 = goldmanLiveBirthProb(age, eggsUsed);

  // Doyle
  const doyle = doyleProbs(modelAge, eggsUsed);

  // Cobo
  const cobo = coboClbrGe1(modelAge, eggsUsed);

  // Combined = average of the EMPIRICAL models only (Doyle & Cobo).
  // Goldman is an idealized upper bound (P_blast × P_euploid × P_live|euploid
  // with no implantation-failure/miscarriage discount) — ensembling it with
  // empirical cohorts inflates the number toward the optimistic bound.
  // It stays computed & returned separately for reference / comparison views.
  const availableModelsP1: number[] = [];
  if (doyle.available) availableModelsP1.push(doyle.p_ge1 / 100);
  if (cobo.available) availableModelsP1.push(cobo.p_ge1 / 100);

  // Fallback: if neither empirical model is applicable (e.g. age outside
  // 30-42), fall back to Goldman so the funnel still has *something* to
  // show rather than 0.
  if (availableModelsP1.length === 0) availableModelsP1.push(goldmanP1);

  const combinedP1 =
    availableModelsP1.reduce((a, b) => a + b, 0) / availableModelsP1.length;

  // For ≥2 live births (only Doyle and Cobo provide this)
  const availableModelsP2: number[] = [];
  if (doyle.available) availableModelsP2.push(doyle.p_ge2 / 100);
  if (cobo.available) availableModelsP2.push(cobo.p_ge2 / 100);

  const combinedP2 =
    availableModelsP2.length > 0
      ? availableModelsP2.reduce((a, b) => a + b, 0) / availableModelsP2.length
      : 0;

  return {
    goldman: {
      p_ge1: pct(goldmanP1),
      available: true,
    },
    doyle,
    cobo,
    combined: {
      p_ge1: pct(combinedP1),
      p_ge2: pct(combinedP2),
      availableCount: availableModelsP1.length,
    },
  };
}
